import { useMemo, useRef, useState } from 'react';
import BarAxisLabels from '@/components/feature/BarAxisLabels';
import ChartGuides from '@/components/feature/ChartGuides';
import useBarAxis from '@/hooks/useBarAxis';
import { useChartKeyboard } from '@/hooks/useChartKeyboard';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { isBarLabeled, selectMobileBarLabels, type YearDivider } from '@/utils/chartAxis';

interface WinRatePoint {
  month: string;
  axisLabel: string;
  year: number;
  trades: number;
  winRate: number;
}

interface MonthlyWinRateBarsProps {
  data: WinRatePoint[];
}

/**
 * Compact per-month win-rate chart. Each bar is the share of that month's
 * closed trades that were winners; the dashed line marks the 50% break-even
 * mark so you can see at a glance which months the edge held up.
 */
export default function MonthlyWinRateBars({ data }: MonthlyWinRateBarsProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState<number | null>(null);
  const narrow = useMediaQuery('(max-width: 640px)');
  const n = data.length;
  const mobileLabels = useMemo(() => new Set(selectMobileBarLabels(n, 3)), [n]);

  // Keep the axis readable: 5 ticks on wide screens, 3 on narrow ones.
  const ticks = narrow ? [100, 50, 0] : [100, 75, 50, 25, 0];
  const gridTicks = narrow ? [50] : [75, 50, 25];
  const positionOf = (value: number) => (1 - value / 100) * 100;

  /* Guide positions measured from the real bar cells, so the crosshair, year
     dividers and tick dots line up with the flex gap instead of drifting. */
  const { ref: barsRef, centers } = useBarAxis(n);
  const ready = centers.length === n;
  const centerAt = (i: number) => centers[i] ?? ((i + 0.5) / n) * 100;
  const yearDividers: YearDivider[] = ready
    ? data.reduce<YearDivider[]>((acc, point, i) => {
        if (i > 0 && point.year !== data[i - 1].year) {
          acc.push({ x: (centers[i - 1] + centers[i]) / 2, label: String(point.year) });
        }
        return acc;
      }, [])
    : [];
  const tickPositions = ready ? centers.filter((_, i) => isBarLabeled(i, n)) : [];

  const handleKeyDown = useChartKeyboard(n, setActive);

  const setActiveFromClientX = (clientX: number) => {
    const el = containerRef.current;
    if (!el || n === 0) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = (clientX - rect.left) / rect.width;
    const index = Math.min(n - 1, Math.max(0, Math.floor(ratio * n)));
    setActive(index);
  };

  return (
    <div className="w-full">
      <div className="flex gap-2 md:gap-3">
        {/* Y axis tick labels */}
        <div className="relative h-[200px] w-9 shrink-0 md:w-11">
          {ticks.map((tick) => (
            <span
              key={tick}
              className="absolute right-0 -translate-y-1/2 whitespace-nowrap font-num text-[10px] font-medium text-foreground-400 md:text-[11px]"
              style={{ top: `${positionOf(tick)}%` }}
            >
              {tick}%
            </span>
          ))}
        </div>

        <div className="min-w-0 flex-1">
          <div className="relative h-[200px]">
            {/* horizontal gridlines (break-even 50% line stays dashed) */}
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
              {gridTicks.map((tick) =>
                tick === 50 ? (
                  <div
                    key={tick}
                    className="absolute left-0 right-0 border-t border-dashed border-background-300"
                    style={{ top: `${positionOf(tick)}%` }}
                  ></div>
                ) : (
                  <div
                    key={tick}
                    className="absolute left-0 right-0 border-t border-background-200/70"
                    style={{ top: `${positionOf(tick)}%` }}
                  ></div>
                ),
              )}
            </div>

            <div
              ref={containerRef}
              className="relative h-full rounded-md outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
              role="img"
              aria-label="Monthly win rate — use the left and right arrow keys to step through each month"
              tabIndex={0}
              onKeyDown={handleKeyDown}
              onMouseLeave={() => setActive(null)}
              onTouchStart={(event) => setActiveFromClientX(event.touches[0]?.clientX ?? 0)}
              onTouchMove={(event) => setActiveFromClientX(event.touches[0]?.clientX ?? 0)}
            >
              <div ref={barsRef} className="flex h-full items-end gap-1.5 md:gap-2">
                {data.map((point, i) => {
                  const above = point.winRate >= 50;
                  const height = Math.max(2, Math.min(100, point.winRate));
                  const isActive = active === i;
                  return (
                    <div
                      key={point.month}
                      className="group relative flex h-full flex-1 cursor-pointer flex-col justify-end"
                      onMouseEnter={() => setActive(i)}
                    >
                      <div
                        className={`pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-primary-950 px-2 py-1 font-num text-[11px] font-semibold text-background-50 transition-opacity ${
                          isActive ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        {point.winRate.toFixed(1)}% · {point.trades} trades
                      </div>
                      <div
                        className={`w-full rounded-t-md transition-all duration-300 ${
                          above
                            ? 'bg-gradient-to-t from-primary-500 to-primary-400'
                            : 'bg-gradient-to-t from-secondary-400 to-secondary-300'
                        } ${isActive ? 'opacity-80' : ''}`}
                        style={{ height: `${height}%` }}
                      ></div>
                    </div>
                  );
                })}
              </div>

              {ready && (
                <ChartGuides
                  yearDividers={yearDividers}
                  ticks={tickPositions}
                  crosshair={active !== null ? centerAt(active) : null}
                  ticksBelow
                  accent="primary"
                />
              )}
            </div>
          </div>

          <div className="mt-3">
            <BarAxisLabels points={data} active={active} mobileShow={mobileLabels} />
          </div>
        </div>
      </div>
    </div>
  );
}